import {
  MousePointerClick,
  LayoutTemplate,
  FileInput,
  Send,
  Repeat,
  Trophy,
  Repeat2,
  Layers,
  BarChart3,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { FaGoogle, FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

const G_COLORS = ["#4285f4", "#ea4335", "#fbbc04", "#34a853"];

const FLOW = [
  { label: "Ad Click", icon: <MousePointerClick className="h-4 w-4" />, color: "#4285f4" },
  { label: "Landing Page", icon: <LayoutTemplate className="h-4 w-4" />, color: "#ea4335" },
  { label: "Lead Capture", icon: <FileInput className="h-4 w-4" />, color: "#fbbc04" },
  { label: "Follow-Up", icon: <Send className="h-4 w-4" />, color: "#34a853" },
  { label: "Remarketing", icon: <Repeat className="h-4 w-4" />, color: "#1a73e8" },
  { label: "Sale", icon: <Trophy className="h-4 w-4" />, color: "#ea4335" },
];

function AnimatedFunnelFlow() {
  return (
    <div className="relative rounded-3xl border border-zinc-200 bg-gradient-to-br from-white to-zinc-50 p-6 sm:p-8 overflow-hidden">
      <div aria-hidden className="absolute -top-10 -right-10 h-44 w-44 rounded-full bg-[#4285f4]/10 blur-3xl" />
      <div aria-hidden className="absolute -bottom-10 -left-10 h-44 w-44 rounded-full bg-[#fbbc04]/15 blur-3xl" />

      <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-5">
        The journey we connect
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {FLOW.map((step, i) => (
          <div key={step.label} className="flex items-center gap-2 sm:gap-3">
            <div
              className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-sm text-zinc-900 shadow-sm"
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <span style={{ color: step.color }}>{step.icon}</span>
              <span className="font-medium">{step.label}</span>
            </div>
            {i < FLOW.length - 1 && (
              <ArrowRight className="h-4 w-4 text-zinc-300 shrink-0" />
            )}
          </div>
        ))}
      </div>

      {/* Animated traveling dot below */}
      <div className="mt-6 relative h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: "30%",
            background: "linear-gradient(90deg,#4285f4,#34a853,#fbbc04,#ea4335)",
            animation: "bar-shift 4.5s ease-in-out infinite",
          }}
        />
      </div>
      <style>{`
        @keyframes bar-shift {
          0% { transform: translateX(-30%); }
          50% { transform: translateX(280%); }
          100% { transform: translateX(-30%); }
        }
      `}</style>
    </div>
  );
}

export function ProblemSection() {
  return (
    <section
      data-testid="problem-section"
      className="relative py-20 sm:py-28 bg-white"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-700 font-semibold shadow-sm mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ea4335]" />
            The Real Problem
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Running ads is easy. Getting good leads is the hard part.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 hover:shadow-sm transition-shadow">
            <div className="h-1 w-10 rounded-full bg-[#ea4335] mb-4" />
            <div className="font-display text-lg font-bold text-zinc-900">Targeting too broad</div>
            <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">
              Budget burns on people who were never going to enquire.
            </p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 hover:shadow-sm transition-shadow">
            <div className="h-1 w-10 rounded-full bg-[#fbbc04] mb-4" />
            <div className="font-display text-lg font-bold text-zinc-900">Landing page mismatched</div>
            <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">
              The ad promises one thing, the page shows another. Visitors leave.
            </p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 hover:shadow-sm transition-shadow">
            <div className="h-1 w-10 rounded-full bg-[#4285f4] mb-4" />
            <div className="font-display text-lg font-bold text-zinc-900">Tracking is missing</div>
            <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">
              You can't optimise what you don't measure. Decisions get made on gut feel.
            </p>
          </div>
        </div>

        <p className="mt-8 max-w-3xl mx-auto text-center text-zinc-600 leading-relaxed text-[15px] sm:text-base">
          Rekisa connects the full paid ads journey so your budget works with a clear goal, clear tracking,
          and a better path from click to enquiry.
        </p>

        <div className="mt-12">
          <AnimatedFunnelFlow />
        </div>
      </div>
    </section>
  );
}

const BENEFITS = [
  { title: "Paid Ads Strategy", desc: "We define the offer, audience, platform, budget and conversion path before ads go live.", Icon: Layers, color: "#4285f4" },
  { title: "Google Ads", desc: "Reach people already searching for your product or solution.", Icon: FaGoogle, brand: true, color: "#4285f4" },
  { title: "Meta Ads", desc: "Reach and retarget people across Facebook and Instagram with offer-led campaigns.", Icon: FaFacebookF, brand: true, color: "#1877f2" },
  { title: "LinkedIn Ads", desc: "Target B2B decision-makers by role, company, industry and location.", Icon: FaLinkedinIn, brand: true, color: "#0a66c2" },
  { title: "Remarketing", desc: "Bring back people who visited, clicked, or engaged but didn't enquire.", Icon: Repeat2, color: "#ea4335" },
  { title: "Landing Pages", desc: "Send traffic to a focused page that matches the ad and makes the next step clear.", Icon: LayoutTemplate, color: "#fbbc04" },
  { title: "Automated Follow-Up", desc: "WhatsApp, email, CRM workflows or SMS — whatever fits your sales process.", Icon: Send, color: "#34a853" },
  { title: "Tracking & Reporting", desc: "Form submissions, calls, key actions, cost per lead and campaign performance.", Icon: BarChart3, color: "#1a73e8" },
];

export function BenefitsSection() {
  return (
    <section
      id="what-we-do"
      data-testid="benefits-section"
      className="relative py-24 sm:py-32 bg-zinc-50/60 border-y border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">
            What we set up
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Everything your ads need to actually work.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {BENEFITS.map(({ title, desc, Icon, brand, color }, idx) => (
            <div
              key={title}
              data-testid={`benefit-card-${idx}`}
              className="group relative rounded-2xl border border-zinc-200 bg-white p-6 hover:border-zinc-300 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] transition-all min-h-[200px] flex flex-col overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-8 -right-8 h-24 w-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl"
                style={{ background: `${color}30` }}
              />
              <div className="flex items-start justify-between mb-6 relative">
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ background: `${color}15`, color }}
                >
                  <Icon className={brand ? "h-4 w-4" : "h-5 w-5"} />
                </div>
                <span className="text-[10px] font-mono text-zinc-300 mt-1">0{idx + 1}</span>
              </div>
              <h3 className="font-display text-xl font-bold text-zinc-900 tracking-tight relative">{title}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed relative">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHO_FOR = [
  "Service businesses that need more enquiries",
  "B2B companies that need qualified leads",
  "eCommerce businesses that need more online sales",
  "Training providers that need registrations",
  "Consultants and professional service firms",
  "Businesses running ads but not getting results",
  "Businesses that want tracking before scaling spend",
  "Businesses that want remarketing to bring back prospects",
];

function AnimatedTargetGraphic() {
  return (
    <div aria-hidden className="relative h-64 w-64 sm:h-80 sm:w-80 mx-auto">
      <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#4285f4]/30 spin-slow" />
      <div className="absolute inset-6 rounded-full border-2 border-dashed border-[#ea4335]/30 spin-slow" style={{ animationDirection: "reverse", animationDuration: "30s" }} />
      <div className="absolute inset-12 rounded-full border-2 border-dashed border-[#fbbc04]/40 spin-slow" style={{ animationDuration: "40s" }} />
      <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 h-20 w-20 -ml-10 -mt-10 rounded-full bg-gradient-to-br from-[#4285f4] via-[#34a853] to-[#fbbc04] flex items-center justify-center shadow-[0_20px_40px_-15px_rgba(26,115,232,0.5)]">
        <CheckCircle2 className="h-10 w-10 text-white" />
      </div>
      {/* Orbiting dots */}
      <div className="absolute inset-1/2" style={{ "--orbit-r": "100px" }}>
        <span className="absolute h-3 w-3 rounded-full bg-[#4285f4] orbit-dot" />
      </div>
      <div className="absolute inset-1/2" style={{ "--orbit-r": "130px" }}>
        <span className="absolute h-2.5 w-2.5 rounded-full bg-[#ea4335] orbit-dot" style={{ animationDuration: "16s", animationDelay: "-3s" }} />
      </div>
      <div className="absolute inset-1/2" style={{ "--orbit-r": "115px" }}>
        <span className="absolute h-2 w-2 rounded-full bg-[#34a853] orbit-dot" style={{ animationDuration: "10s", animationDelay: "-6s" }} />
      </div>
    </div>
  );
}

export function WhoForSection() {
  return (
    <section
      data-testid="who-for-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Who this is for</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Made for teams that need more leads.
          </h2>
          <div className="mt-10 hidden lg:block">
            <AnimatedTargetGraphic />
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {WHO_FOR.map((item, i) => (
            <li
              key={item}
              data-testid={`who-for-item-${i}`}
              className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-4 hover:border-zinc-300 hover:shadow-sm transition-all"
            >
              <span
                className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: `${G_COLORS[i % 4]}15`, color: G_COLORS[i % 4] }}
              >
                <CheckCircle2 className="h-4 w-4" />
              </span>
              <span className="text-[15px] text-zinc-800 leading-snug">{item}</span>
            </li>
          ))}
        </ul>
        <div className="lg:hidden">
          <AnimatedTargetGraphic />
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { title: "Define the Goal", desc: "One clear goal — quotes, enquiries, bookings, registrations or sales.", color: "#4285f4" },
  { title: "Choose the Audience", desc: "Built around search intent, interests, job titles, location or customer data.", color: "#ea4335" },
  { title: "Select Platforms", desc: "Google, Meta, LinkedIn, remarketing — or a mix that matches the goal.", color: "#fbbc04" },
  { title: "Build the Campaigns", desc: "Structure, targeting, budget, ads, conversions and tracking setup.", color: "#34a853" },
  { title: "Create Ad Variations", desc: "Focused variations that test messaging without spreading the budget thin.", color: "#4285f4" },
  { title: "Match the Landing Page", desc: "The page matches the ad message, offer, and search intent.", color: "#ea4335" },
  { title: "Launch With Control", desc: "Controlled budgets, clear limits, and campaign checks to avoid waste.", color: "#fbbc04" },
  { title: "Optimise Monthly", desc: "Search terms, audiences, creatives, placements, CPL and lead quality.", color: "#34a853" },
];

export function ProcessSection() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-24 sm:py-32 bg-zinc-50/60 border-y border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Our process</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
              From brief to better leads. In eight steps.
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {PROCESS.map((step, i) => (
            <div
              key={step.title}
              data-testid={`process-step-${i + 1}`}
              className="relative rounded-2xl border border-zinc-200 bg-white p-6 hover:border-zinc-300 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] transition-all overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ background: step.color }}
              />
              <span
                aria-hidden
                className="font-display absolute -right-2 -top-3 text-[110px] leading-none font-black select-none"
                style={{ color: `${step.color}10` }}
              >
                0{i + 1}
              </span>
              <div className="relative">
                <div
                  className="text-[10px] tracking-[0.2em] uppercase font-mono mb-3 font-bold"
                  style={{ color: step.color }}
                >
                  Step 0{i + 1}
                </div>
                <h3 className="font-display text-xl font-bold text-zinc-900 tracking-tight">{step.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
